import { Link } from "react-router-dom";
import { LEGAL_UPDATED, OPERATOR_NAME } from "../../../shared/legal";
import { ArrowIcon, CrossIcon } from "../../lib/ui";
import { LegalPage } from "./LegalPage";
import { Chips, Clause, Label, PrivacyMail, Row } from "./parts";

const CONTENTS: [string, string][] = [
  ["c1", "Who we are"],
  ["c2", "What we collect"],
  ["c3", "Why we use it"],
  ["c4", "Who sees it"],
  ["c5", "Overseas transfers"],
  ["c6", "How long we keep it"],
  ["c7", "Keeping it safe"],
  ["c8", "Your rights"],
  ["c9", "Cookies and storage"],
  ["c10", "Children"],
  ["c11", "Changes and contact"],
];

const NEVER = ["Your money", "Your bank login or password", "Card numbers", "NRIC or FIN numbers", "The rest of your email"];

export default function Privacy() {
  return (
    <LegalPage tab="privacy" title="Privacy Policy">
      <section className="wrap" style={{ paddingTop: 48, paddingBottom: 56 }}>
        <div className="lg-eyebrow">Privacy Policy</div>
        <h1 className="display" style={{ marginTop: 18, maxWidth: 980, fontSize: "min(76px, 11vw)" }}>
          What we collect, why, and when it’s gone.
        </h1>
        <p className="lead" style={{ marginTop: 26, maxWidth: 700 }}>
          ConfirmPay helps Singapore nonprofits and small businesses ask for PayNow payments and see when they land. It is built to know as little as possible about you. This page lists what it does know, who sees it, and how to take it back.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 30 }}>
          <span className="lg-pill">Last updated {LEGAL_UPDATED}</span>
          <span className="lg-pill">Singapore law · PDPA 2012</span>
          <span className="lg-pill">Database hosted in Singapore</span>
        </div>
      </section>

      <section className="wrap" style={{ paddingBottom: 64 }} aria-label="Contents">
        <div className="lg-eyebrow" style={{ marginBottom: 14 }}>Jump to</div>
        <ol className="lg-toc">
          {CONTENTS.map(([id, label], i) => (
            <li key={id}>
              <a href={`#${id}`}>
                <span>{String(i + 1).padStart(2, "0")}</span>
                {label}
              </a>
            </li>
          ))}
        </ol>
      </section>

      <section className="wrap" style={{ paddingBottom: 88 }}>
        <div className="lg-stripes">
          <div className="lg-stripe"><h3>No money.</h3><p>Payments go bank to bank. We never hold, move or see the funds.</p></div>
          <div className="lg-stripe"><h3>No secrets.</h3><p>We never ask for a bank login, card number, password or NRIC/FIN.</p></div>
          <div className="lg-stripe"><h3>No selling, no ad tracking.</h3><p>We don’t sell personal data, show ads or run analytics trackers.</p></div>
          <div className="lg-stripe"><h3>Yours to take back.</h3><p>Ask what we hold, correct it, or have it deleted. Free, and answered within 30 days.</p></div>
        </div>
      </section>

      <section className="wrap" style={{ paddingBottom: 72 }}>
        <div className="lg-eyebrow" style={{ marginBottom: 14 }}>Who’s who</div>
        <h2 className="h2" style={{ maxWidth: 760, fontSize: "min(48px, 9vw)" }}>Three roles, and who answers for what.</h2>
        <div className="lg-cast" style={{ marginTop: 36 }}>
          <div>
            <span className="lg-eyebrow">You run an organisation</span>
            <h3>Organisation</h3>
            <p>You decide who you ask to pay and what you do with their details. Under the PDPA you answer for the payer details you collect. ConfirmPay holds them on your behalf.</p>
          </div>
          <div>
            <span className="lg-eyebrow">You are paying</span>
            <h3>Payer</h3>
            <p>You give a name, and an email if you want a receipt, so the organisation can match and thank your payment. You never need an account.</p>
          </div>
          <div>
            <span className="lg-eyebrow">We run the service</span>
            <h3>ConfirmPay</h3>
            <p>For payer details we act as the organisation’s data intermediary: we hold and protect them for it. For organisation accounts and security, we decide how data is used and answer for it.</p>
          </div>
        </div>
      </section>

      <div className="wrap">
        <Clause n="01" id="c1" title="Who we are" short="A small Singapore-run service, run by one person. Contact details are at the bottom of the page." first>
          <p>
            In this policy, <b>ConfirmPay</b>, <b>we</b> and <b>us</b> mean {OPERATOR_NAME}, an individual in Singapore who runs confirmpay.link. This policy explains how we collect, use, disclose and look after personal data under Singapore’s <b>Personal Data Protection Act 2012</b> (the <b>PDPA</b>). “Personal data” means data about a person who can be identified from it.
          </p>
          <p>
            Our Data Protection Officer is {OPERATOR_NAME}, reachable at <PrivacyMail />.
          </p>
          <p>This policy applies to organisations that sign up, to people who pay through a ConfirmPay link, and to visitors to confirmpay.link.</p>
        </Clause>

        <Clause n="02" id="c2" title="What we collect" short="Only what a payment request needs. From a bank email: a few details, never the message." tight>
          <p>
            We collect personal data in four ways: what you type into our forms; what is generated when you use the service, such as your sign-in session and request records; details taken from bank notification emails that an organisation chooses to forward to its private ConfirmPay address; and details an organisation enters when it creates a request for you. We do not buy personal data from anyone.
          </p>
          <p>
            <b>What we don’t collect.</b> We never ask for bank logins, card numbers, passwords to other services, or NRIC/FIN numbers. Please don’t type them into free-text boxes such as “what it’s for”.
          </p>
          <p>
            <b>Forwarded bank emails.</b> From each forwarded email we keep only the fields in the table below. We do not store the body of the email, its attachments or any other part of it, and there is no mailbox for it to sit in: it is read when it arrives.
          </p>
        </Clause>
        <div className="lg-vis">
          <div className="lg-led" role="list" aria-label="Personal data we hold">
            <Row
              a={<><Label>Data</Label><Chips items={["Name", "Contact email", "PayNow mobile number or UEN", "Link name", "Reference prefix"]} /></>}
              b={<><Label>From · Why</Label><p><b>Organisations.</b> To open the account, build your PayNow QR codes, sign you in and send you service emails.</p></>}
              c={<><Label>Kept</Label><p>While the account is open</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["Name", "Email (optional)", "Amount", "What it’s for"]} /></>}
              b={<><Label>From · Why</Label><p><b>Payers,</b> or the organisation making a request for them. To create the request, match the payment and send a receipt.</p></>}
              c={<><Label>Kept</Label><p>With the organisation’s records</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["Amount", "Reference", "Payer’s name as the bank printed it", "Sender’s address", "Message ID", "Result of the sender checks"]} /></>}
              b={<><Label>From · Why</Label><p><b>Forwarded bank emails.</b> To mark requests paid, never count one email twice, and keep an audit trail.</p></>}
              c={<><Label>Kept</Label><p>While the account is open</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["Email address", "Hashed sign-in code"]} /></>}
              b={<><Label>From · Why</Label><p><b>Anyone signing in.</b> To prove you can read the inbox. We keep only a hash, so a code can’t be read back.</p></>}
              c={<><Label>Kept</Label><p>Code valid for 10 minutes; record cleared within days</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["PayNow mobile number", "Hashed code", "Sender’s number, if different"]} /></>}
              b={<><Label>From · Why</Label><p><b>Organisations connecting a mobile number.</b> To prove the number is yours, using a WhatsApp message sent from it.</p></>}
              c={<><Label>Kept</Label><p>Cleared within days</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["Hashed network address", "Request counts"]} /></>}
              b={<><Label>From · Why</Label><p><b>Everyone.</b> To stop spam and code-guessing. We store a keyed hash of the address, not the address.</p></>}
              c={<><Label>Kept</Label><p>Minutes to a day</p></>}
            />
            <Row
              a={<><Label>Data</Label><Chips items={["Your message to us"]} /></>}
              b={<><Label>From · Why</Label><p><b>Anyone who writes.</b> To reply and keep a record of what we agreed.</p></>}
              c={<><Label>Kept</Label><p>Until resolved, then deleted unless the law needs it kept</p></>}
            />
          </div>
        </div>
      </div>

      <section className="band">
        <div className="wrap" style={{ paddingTop: 72, paddingBottom: 72 }}>
          <div className="lg-eyebrow" style={{ color: "var(--band-mute)" }}>What we never collect</div>
          <h2 className="h2" style={{ marginTop: 14, maxWidth: 700, fontSize: "min(48px, 9vw)" }}>Things ConfirmPay never takes.</h2>
          <div className="lg-never" style={{ marginTop: 40 }}>
            {NEVER.map((t) => (
              <div key={t}>
                <CrossIcon size={22} />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="wrap" style={{ paddingTop: 24 }}>
        <Clause n="03" id="c3" title="Why we use it" short="To run the service. Not to market to you, and not to profile you." first>
          <p>We use personal data only to:</p>
          <ol>
            <li>open and run organisation accounts, and sign people in;</li>
            <li>create PayNow payment requests, links and QR codes;</li>
            <li>recognise payments and mark requests paid;</li>
            <li>send sign-in codes, receipts and service notices;</li>
            <li>keep the service secure and prevent fraud, spam and abuse;</li>
            <li>meet legal obligations and answer lawful requests from authorities; and</li>
            <li>understand how reliable the service is, using counts that don’t identify anyone.</li>
          </ol>
          <p>
            <b>Consent.</b> By signing up, typing your details into a payment form, or forwarding bank emails, you consent to us collecting, using and disclosing your personal data as this policy describes. If you give us someone else’s personal data, for example an organisation adding a payer’s email, you confirm you are entitled to. You can withdraw consent at any time; see “Your rights”.
          </p>
          <p>
            <b>No marketing.</b> We don’t send newsletters or promotions, and we won’t add anyone to a mailing list.
          </p>
        </Clause>

        <Clause n="04" id="c4" title="Who sees it" short="The organisation you pay sees your payer details. A few providers run the plumbing. Nobody gets it for marketing." tight>
          <p>We disclose personal data only:</p>
          <ol>
            <li><b>to the organisation you paid,</b> which sees the payer details on its own requests;</li>
            <li><b>to service providers</b> who process data for us under written terms that limit them to providing their service (listed below);</li>
            <li><b>where the law requires it,</b> such as a court order or a request from a regulator or the police, or to protect people from harm; and</li>
            <li><b>in a business transfer,</b> such as a merger or sale, where the new owner must keep to this policy and we will tell organisations first.</li>
          </ol>
          <p><b>We do not sell personal data.</b></p>
        </Clause>
        <div className="lg-vis">
          <div className="lg-led" role="list" aria-label="Service providers">
            <Row a={<><p className="lg-name">Cloudflare</p><p>Hosting, email routing and DNS.</p></>} b={<><Label>Sees</Label><p>Web requests, including network addresses, and forwarded bank emails in transit.</p></>} c={<><Label>Where</Label><p>Global network</p></>} />
            <Row a={<><p className="lg-name">Supabase</p><p>Our database.</p></>} b={<><Label>Sees</Label><p>The records in the table above.</p></>} c={<><Label>Where</Label><p>Singapore (AWS ap-southeast-1)</p></>} />
            <Row a={<><p className="lg-name">Resend</p><p>Sends our emails.</p></>} b={<><Label>Sees</Label><p>The recipient’s address and the email itself: codes, receipts and notices.</p></>} c={<><Label>Where</Label><p>United States</p></>} />
            <Row a={<><p className="lg-name">Meta (WhatsApp)</p><p>Only when a mobile number is confirmed on WhatsApp.</p></>} b={<><Label>Sees</Label><p>The sender’s phone number and the code message.</p></>} c={<><Label>Where</Label><p>Meta’s servers, outside Singapore</p></>} />
            <Row a={<><p className="lg-name">Cloudflare Turnstile</p><p>Bot check on public payment links.</p></>} b={<><Label>Sees</Label><p>Your network address and browser signals Cloudflare uses to tell people from bots.</p></>} c={<><Label>Where</Label><p>Global network</p></>} />
          </div>
        </div>

        <Clause n="05" id="c5" title="Overseas transfers" short="Our database is in Singapore. Some providers handle data elsewhere, under terms that keep PDPA-level protection." first>
          <p>
            Some providers listed above handle personal data outside Singapore. Before personal data leaves Singapore, we take steps to make sure the recipient gives it protection comparable to the PDPA, as the Personal Data Protection Regulations 2021 require, including by contract with the provider.
          </p>
          <p>If you’d like to know which providers handle your data for a given purpose, ask our Data Protection Officer.</p>
        </Clause>

        <Clause n="06" id="c6" title="How long we keep it" short="Short-lived things disappear fast. An account’s records stay while it’s open, and go within 30 days of closing." tight>
          <p>We keep personal data only as long as we need it for the purposes above, or for legal or business reasons. In practice:</p>
          <ul>
            <li>Sign-in codes work for 10 minutes, and the record is cleared within days.</li>
            <li>WhatsApp number checks and abuse counters are cleared within days.</li>
            <li>An organisation’s details, requests and bank-email records stay while its account is open.</li>
            <li>When an organisation closes its account, or asks us to, we delete or anonymise its data within 30 days.</li>
            <li>When a payer asks, we remove their name and email from requests within 30 days. The amount, reference and date stay, because they are the organisation’s record of the payment.</li>
          </ul>
          <p>
            We may keep data longer where the law requires it, or where we need it to establish, exercise or defend a legal claim. Copies in routine database backups age out on the backup provider’s normal schedule and are not restored except to recover from a failure.
          </p>
        </Clause>
        <div className="lg-vis">
          <div className="lg-ladder" role="list" aria-label="How long data is kept, from shortest to longest. Not to scale.">
            <div role="listitem"><div className="lg-bar"><i style={{ width: "8%" }} /></div><h4>Minutes</h4><p>Sign-in codes, valid for 10 minutes.</p></div>
            <div role="listitem"><div className="lg-bar"><i style={{ width: "26%" }} /></div><h4>A few days</h4><p>WhatsApp number checks, abuse counters and used sign-in code records.</p></div>
            <div role="listitem"><div className="lg-bar"><i style={{ width: "70%" }} /></div><h4>While the account is open</h4><p>Organisation details, requests, payer details and bank-email records.</p></div>
            <div role="listitem"><div className="lg-bar"><i style={{ width: "100%" }} /></div><h4>30 days after you close it</h4><p>All of the above deleted or anonymised. Backups age out; legal holds excepted.</p></div>
          </div>
          <p style={{ margin: "16px 0 0", fontFamily: "var(--mono)", fontSize: 12.5, color: "var(--ink3)" }}>Bars show order, not scale.</p>
        </div>

        <Clause n="07" id="c7" title="Keeping it safe" short="Locked by default, codes that can’t be reused, and nothing stored that we don’t need." first>
          <p>We protect personal data with reasonable security arrangements, as the PDPA requires. These include:</p>
          <ul>
            <li>encryption in transit (HTTPS) on every page and request;</li>
            <li>a database with no public access: only our servers can read or write it;</li>
            <li>sign-in codes that expire in 10 minutes, allow five tries, and are stored only as hashes;</li>
            <li>secure, HttpOnly session cookies, and a “sign out everywhere” button that ends every other session;</li>
            <li>rate limits against guessing and spam, and a manual check for new organisations we can’t verify automatically; and</li>
            <li>not storing what we don’t need, such as the body of forwarded emails.</li>
          </ul>
          <p>
            <b>If something goes wrong.</b> If a data breach is likely to cause significant harm, or affects 500 or more people, we will tell the Personal Data Protection Commission (PDPC) within 3 calendar days of deciding it is notifiable, and tell affected people as soon as we can. If we hold the data for an organisation, we will tell that organisation without undue delay.
          </p>
          <p>
            No system is perfectly secure. If you spot a security problem, please tell us at <PrivacyMail />.
          </p>
        </Clause>

        <Clause n="08" id="c8" title="Your rights" short="Ask what we hold, fix it, take back consent, or have it deleted. Free, and answered within 30 days.">
          <ol>
            <li><b>See your data (access).</b> Ask for a copy of the personal data we hold about you, and how we have used or disclosed it in the past year. We reply within 30 days, or tell you when we can. We don’t charge a fee.</li>
            <li><b>Correct it.</b> If something is wrong, tell us. We fix it as soon as practicable and, where needed, tell the organisations we shared it with in the past year.</li>
            <li><b>Withdraw consent.</b> Tell us, and we’ll explain what it means (for example, we can’t run an account without a contact email) and stop within a reasonable time. We aim for 10 business days.</li>
            <li><b>Delete it.</b> The PDPA has no blanket right to erasure, but we act on deletion requests by treating them as a withdrawal of consent and applying our duty not to keep data longer than needed. Use the form below.</li>
            <li><b>Complain.</b> Contact our Data Protection Officer first. If we can’t sort it out, you can complain to the PDPC at pdpc.gov.sg.</li>
          </ol>
          <p>
            <b>Payers.</b> The organisation you paid decides what to do with the record of your payment. We can remove your name and email from it ourselves; ask using the delete form.
          </p>
          <div className="lg-cta">
            <p>Want your data deleted? It takes one short form.</p>
            <Link className="btn" to="/delete-data">
              Ask us to delete your data
              <ArrowIcon size={20} />
            </Link>
          </div>
        </Clause>

        <Clause n="09" id="c9" title="Cookies and storage" short="Two cookies to keep you signed in. No advertising or analytics." tight>
          <p>
            These are strictly necessary to run the service, so we don’t show a cookie banner. We don’t use advertising or analytics cookies, and we don’t track you across other sites. The payment page runs the Cloudflare Turnstile bot check, so Cloudflare may run its own checks in your browser; see Cloudflare’s privacy policy.
          </p>
        </Clause>
        <div className="lg-vis">
          <div className="lg-led" role="list" aria-label="Cookies and browser storage">
            <Row a={<><p className="lg-name lg-name--mono">cp_session</p><p>Keeps an organisation signed in.</p></>} b={<><Label>Settings</Label><p>HttpOnly · Secure · SameSite=Strict</p></>} c={<><Label>Lasts</Label><p>30 days, or until you sign out</p></>} />
            <Row a={<><p className="lg-name lg-name--mono">cp_pending</p><p>Carries a verified sign-in to the organisation picker.</p></>} b={<><Label>Settings</Label><p>HttpOnly · Secure · SameSite=Strict</p></>} c={<><Label>Lasts</Label><p>2 minutes</p></>} />
            <Row a={<><p className="lg-name lg-name--mono">cp.embedPromptSeen</p><p>Remembers you dismissed a dashboard tip.</p></>} b={<><Label>Settings</Label><p>Browser storage, on your device only</p></>} c={<><Label>Lasts</Label><p>Until you clear it</p></>} />
          </div>
        </div>

        <Clause n="10" id="c10" title="Children" short="Accounts are for adults acting for an organisation." first>
          <p>
            ConfirmPay is for organisations and the adults who run them. We don’t knowingly create accounts for anyone under 18. If you believe a child’s personal data has reached us, contact our Data Protection Officer and we’ll remove it.
          </p>
        </Clause>

        <Clause n="11" id="c11" title="Changes and contact" short="If this changes in a way that matters, you’ll hear about it first.">
          <p>
            We may update this policy. For changes that matter, we’ll email organisations’ contact addresses and update the date at the top at least 14 days before the change takes effect.
          </p>
          <div className="lg-contact">
            <div><Label>Data Protection Officer</Label>{OPERATOR_NAME}</div>
            <div><Label>Email</Label><PrivacyMail /></div>
            <div><Label>Regulator</Label>Personal Data Protection Commission · pdpc.gov.sg</div>
          </div>
        </Clause>
      </div>
    </LegalPage>
  );
}
